import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/brand/Navbar';
import HeroSection from '../components/brand/HeroSection';
import HowItWorksSection from '../components/brand/HowItWorksSection';
import ServicesSection from '../components/brand/ServicesSection';
import StatsTrustBar from '../components/brand/StatsTrustBar';
import WhyNuraSection from '../components/brand/WhyNuraSection';
import FAQSection from '../components/brand/FAQSection';
import CTASection from '../components/brand/CTASection';
import Footer from '../components/brand/Footer';

const homepageFaqs = [
  {
    q: 'What is mobile pathology collection?',
    a: 'Mobile pathology collection is a service where a qualified phlebotomist travels to your home, workplace, or care facility to collect blood samples. Instead of visiting a pathology centre, the collector comes to you with all the equipment needed. Specimens are transported to a partner laboratory and results are sent to your doctor, exactly the same as a traditional collection centre.',
  },
  {
    q: 'What areas does Nura Health service?',
    a: 'We service Brisbane, Sydney and Melbourne metros. If you are unsure whether your location is within our service area, contact us on 0420 424 290 and we will confirm.',
  },
  {
    q: 'How much does a home blood test cost?',
    a: 'A standard home blood collection is $55. Same-day or urgent collection is $75. For NDIS participants, the rate is $60 standard or $80 urgent. Aged care facilities receive volume-based pricing starting from $55 per resident per visit. The collection fee covers the collector visit, specimen handling, and transport to the lab.',
  },
  {
    q: 'Are your collectors qualified?',
    a: 'Yes. Every Nura Health collector holds a Certificate III in Pathology Collection (HLT37215), carries a minimum of $10 million in professional indemnity insurance, has a current police check, and follows procedures aligned to the NPAAC 5th Edition — the national standard for pathology collection and transport in Australia.',
  },
  {
    q: 'How do I book a mobile blood collection?',
    a: 'You can book online at nurahealth.com.au or call us on 0420 424 290. Select your service type, choose a date and time, and a qualified collector will arrive at your location.',
  },
  {
    q: "Do I need a doctor's referral?",
    a: "Yes, you will need a pathology request form from your GP or specialist. This is the same form you would take to any pathology collection centre. If you do not have one, contact your doctor and they can issue one for you before your appointment.",
  },
  {
    q: 'Are there any tests you cannot collect for at home?',
    a: 'Yes. The Glucose Tolerance Test (GTT) cannot be performed as a home visit. GTT requires the patient to remain at a pathology collection centre for up to two hours while timed blood draws are taken after a glucose drink. For GTT, you will need to attend your nearest pathology collection centre. For all other standard blood tests, mobile collection is suitable. If you are unsure about your specific test, call us on 0420 424 290 before booking.',
  },
];

const homepageFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: homepageFaqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const schemaOrg = {
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  "name": "Nura Health",
  "url": "https://nurahealth.com.au",
  "logo": "https://nurahealth.com.au/Nura_Final-04.png",
  "image": "https://nurahealth.com.au/Nura_Final-04.png",
  "description": "Professional mobile blood collection and at-home pathology services across Brisbane, Sydney and Melbourne metros. NPAAC-compliant mobile phlebotomy for individuals, aged care facilities, NDIS participants, and workplaces.",
  "telephone": "+61420424290",
  "email": "hello@nurahealth.com.au",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "25 Walsh St",
    "addressLocality": "Milton",
    "addressRegion": "QLD",
    "postalCode": "4064",
    "addressCountry": "AU"
  },
  "areaServed": [
    { "@type": "City", "name": "Brisbane" },
    { "@type": "City", "name": "Sydney" },
    { "@type": "City", "name": "Melbourne" }
  ],
  "serviceType": [
    "Mobile Blood Collection",
    "At-Home Pathology Collection",
    "Aged Care Pathology Services",
    "NDIS Pathology Collection",
    "Corporate Workplace Blood Testing"
  ],
  "priceRange": "$55-$80",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "07:00",
    "closes": "17:00"
  },
  "sameAs": []
};

const HomePage = () => {
  const navigate = useNavigate();

  const handleBookClick = () => {
    navigate('/booking');
  };

  return (
    <>
      <Helmet>
        <title>Mobile Blood Collection | At-Home Pathology Brisbane, Sydney &amp; Melbourne | Nura Health</title>
        <meta name="description" content="Professional mobile blood collection across Brisbane, Sydney and Melbourne metros. NPAAC-compliant phlebotomists for home visits, aged care facilities & NDIS participants. Book online." />
        <link rel="canonical" href="https://nurahealth.com.au/" />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
        <script type="application/ld+json">{JSON.stringify(homepageFaqSchema)}</script>
      </Helmet>

      {/* Visually-hidden H1 for crawlers — the visual heading is inside HeroSection */}
      <h1 className="sr-only">Mobile Blood Collection Across Brisbane, Sydney and Melbourne</h1>

      <div className="min-h-screen bg-[#F7F9FB]">
        <Navbar onBookClick={handleBookClick} />
        <HeroSection onBookClick={handleBookClick} />
        <HowItWorksSection />
        <ServicesSection />
        <StatsTrustBar />
        <WhyNuraSection onBookClick={handleBookClick} />
        <FAQSection items={homepageFaqs} />
        <CTASection onBookClick={handleBookClick} />
        <Footer />
      </div>
    </>
  );
};

export default HomePage;
